'use client';

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import UserShortCut from 'components/common/UserShortCut';
import { GlobalSearchService } from 'components/services/services';
import { useMediaContext } from 'contexts/MediaContext';
import useClickOutside from 'hooks/useClickOutside';

import clsx from 'clsx';
import LucideIcon from 'components/common/LucideIcon';
import { Search as SearchIcon } from 'lucide-react';
import Tippy from '@tippyjs/react';

interface SearchContainerProps {
  CloseComponent?: () => void;
  usersOnly?: boolean;
  className?: string;
  onUserSelect?: (userId: string | number) => void;
  placeholder?: string;
  alwaysOpen?: boolean;
}

// Constants
const SEARCH_TYPES = {
  USER_DOC: 'UserDoc',
  COMPANY_DOC: 'CompanyDoc',
  USER: 'User',
} as const;

export interface SearchContainerRef {
  clearSearch: () => void;
}

const SearchContainer = forwardRef<SearchContainerRef, SearchContainerProps>(
  (
    {
      CloseComponent,
      usersOnly = false,
      className = '',
      onUserSelect,
      placeholder,
      alwaysOpen = false,
    },
    ref,
  ) => {
    // Hooks
    const { t } = useTranslation();
    const { openMedia } = useMediaContext();
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // State
    const [isSearchOpen, setIsSearchOpen] = useState(alwaysOpen);
    const [searchValue, setSearchValue] = useState<string>('');
    const [documentList, setDocumentList] = useState<
      SelectOptionResponse[] | null
    >(null);
    const [usersList, setUsersList] = useState<SelectOptionResponse[] | null>(
      null,
    );
    const [isSearching, setIsSearching] = useState(false);
    // Results-dropdown visibility, tracked separately from `searchValue` so an
    // outside click can dismiss the results without wiping the input. That
    // matters for `alwaysOpen` consumers (the org chart), where the typed text
    // IS the active query and must survive the dismissal.
    const [isResultsOpen, setIsResultsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      clearSearch: () => {
        setSearchValue('');
        setIsResultsOpen(false);
        if (!alwaysOpen) setIsSearchOpen(false);
      },
    }));

    // Handlers
    const handleInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const value = e.target.value;
        setSearchValue(value);
        setIsResultsOpen(true);

        if (value.trim()) {
          setIsSearching(true);
          try {
            const response = await GlobalSearchService({ value, regex: true });

            const documents = response.filter(
              (item) =>
                item.type === SEARCH_TYPES.USER_DOC ||
                item.type === SEARCH_TYPES.COMPANY_DOC,
            );
            const users = response.filter(
              (item) => item.type === SEARCH_TYPES.USER,
            );

            setDocumentList(documents);
            setUsersList(users);
          } catch (error) {
            console.error('Search error:', error);
            setDocumentList([]);
            setUsersList([]);
          } finally {
            setIsSearching(false);
          }
        } else {
          setDocumentList(null);
          setUsersList(null);
        }
      },
      [],
    );

    const handleClose = useCallback(() => {
      setSearchValue('');
      setDocumentList(null);
      setUsersList(null);
      setIsResultsOpen(false);
      if (!alwaysOpen) setIsSearchOpen(false);

      if (CloseComponent) {
        CloseComponent();
      }
    }, [CloseComponent, alwaysOpen]);

    const handleOpenFile = useCallback(
      (fileName: string, title: string, type: string, id?: number): void => {
        const ext = fileName.split('.').pop() as string;
        const filePath =
          type === SEARCH_TYPES.COMPANY_DOC
            ? `/upload/GetCompanyFile/${id}`
            : `/upload/document/${fileName}`;

        openMedia(filePath, ext, title);
        setIsSearchOpen(false);
      },
      [openMedia],
    );

    const handleUserSelect = useCallback(
      (user: SelectOptionResponse) => {
        if (onUserSelect) {
          onUserSelect(user.value);
        }
        if (!alwaysOpen) setIsSearchOpen(false);
        setSearchValue('');
      },
      [onUserSelect, alwaysOpen],
    );

    // Click outside logic. This used to be gated on `!searchValue`, which meant
    // the one case that matters — the user typed something, then clicked away —
    // left the expanded input and its results dropdown sitting on screen.
    // Now: results always dismiss, and a collapsible search also collapses and
    // resets. `alwaysOpen` consumers keep their input and its text.
    useClickOutside(
      searchContainerRef,
      () => {
        setIsResultsOpen(false);
        if (!alwaysOpen) handleClose();
      },
      isSearchOpen,
    );

    // Auto-focus input when search opens
    useEffect(() => {
      if (isSearchOpen && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    }, [isSearchOpen]);

    useEffect(() => {
      const handleOpenMobileSearch = () => setIsSearchOpen(true);
      window.addEventListener('open-mobile-search', handleOpenMobileSearch);
      return () =>
        window.removeEventListener(
          'open-mobile-search',
          handleOpenMobileSearch,
        );
    }, []);

    useEffect(() => {
      if (!isSearchOpen) {
        window.dispatchEvent(new Event('close-mobile-search'));
      }
    }, [isSearchOpen]);

    const showDropdown = isResultsOpen && searchValue.trim() !== '';

    return (
      <div
        className={clsx(
          'tw-relative tw-flex tw-items-center tw-justify-end',
          className,
        )}
        ref={searchContainerRef}
      >
        {/* Expandable Search Input Container */}
        <div
          className={clsx(
            'tw-relative tw-transition-all tw-duration-300 tw-ease-in-out tw-flex tw-items-center',
            isSearchOpen
              ? alwaysOpen
                ? 'tw-w-full tw-flex'
                : 'mobile:tw-w-full mobile:tw-flex-1 md:tw-w-[340px] sm:tw-w-[280px] tw-flex'
              : 'tw-w-0 md:tw-w-10 tw-flex',
          )}
        >
          {isSearchOpen ? (
            <div className="tw-relative tw-w-full tw-animate-in tw-fade-in tw-zoom-in-95 tw-duration-200">
              <input
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={handleInputChange}
                // Re-open results after an outside click dismissed them, without
                // the user having to retype.
                onFocus={() => setIsResultsOpen(true)}
                className={clsx(
                  'tw-w-full tw-outline-none tw-transition-all tw-duration-200',
                  alwaysOpen
                    ? 'tw-h-[calc(var(--unit-minthr)*40)] tw-ps-[calc(var(--unit-minthr)*42)] tw-pe-[calc(var(--unit-minthr)*42)] tw-rounded-[calc(var(--unit-minthr)*5)] tw-border tw-border-light-gray2 tw-bg-white tw-text-dark-gray3 tw-placeholder-dark-gray5 tw-text-[calc(var(--unit-minthr)*11)] tw-font-medium tw-tracking-wide focus:tw-border-primary'
                    : 'tw-h-[36px] tw-ps-[34px] tw-pe-[32px] tw-rounded-md tw-text-[13px] tw-border-[0.5px] tw-border-gray-200 tw-bg-white tw-text-gray-800 tw-placeholder-gray-400 focus:tw-border-brand-500 focus:tw-ring-0',
                )}
                placeholder={placeholder || t('Search')}
              />
              {alwaysOpen ? (
                <>
                  <img
                    src="/icons/search.svg"
                    alt=""
                    className={clsx(
                      'tw-absolute tw-start-[calc(var(--unit-minthr)*13)] tw-top-1/2 tw--translate-y-1/2 tw-w-[calc(var(--unit-minthr)*19)] tw-h-[calc(var(--unit-minthr)*19)] tw-pointer-events-none tw-transition-all tw-duration-200',
                      searchValue
                        ? 'tw-filter-none'
                        : 'tw-brightness-[1.92] tw-grayscale',
                    )}
                  />
                  {searchValue && (
                    <button
                      onClick={() => {
                        setSearchValue('');
                        setDocumentList(null);
                        setUsersList(null);
                        searchInputRef.current?.focus();
                      }}
                      className="tw-absolute tw-end-[calc(var(--unit-minthr)*13)] tw-top-1/2 tw--translate-y-1/2 tw-w-[calc(var(--unit-minthr)*15)]  tw-border-none tw-bg-transparent tw-cursor-pointer tw-p-0"
                    >
                      <img
                        src="/icons/clear.svg"
                        alt=""
                        className="tw-w-full tw-h-full tw-object-contain"
                      />
                    </button>
                  )}
                </>
              ) : (
                <>
                  <SearchIcon
                    size={16}
                    strokeWidth={1.8}
                    className="tw-absolute tw-start-[10px] tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none"
                  />
                  {searchValue && (
                    <button
                      onClick={() => {
                        setSearchValue('');
                        searchInputRef.current?.focus();
                      }}
                      className="tw-absolute tw-end-[6px] tw-top-1/2 tw--translate-y-1/2 tw-w-[20px] tw-h-[20px] tw-flex tw-items-center tw-justify-center tw-rounded-[4px] hover:tw-bg-gray-100 tw-text-gray-400 tw-transition-colors"
                    >
                      <LucideIcon name="X" size={12} />
                    </button>
                  )}
                </>
              )}

              {/* Desktop Search Dropdown */}
              {showDropdown && (
                <div
                  className={clsx(
                    'tw-absolute tw-top-full tw-left-0 tw-mt-[8px] tw-w-full tw-bg-white tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] tw-border-[0.5px] tw-border-gray-200 tw-overflow-hidden tw-z-[200] tw-animate-in tw-fade-in tw-zoom-in-95 tw-slide-in-from-top-2 tw-duration-200 tw-text-gray-700 minthr-search-result',
                    !(
                      usersList?.length ||
                      (!usersOnly && documentList?.length)
                    ) && 'search-no-result',
                  )}
                >
                  {/* Premium Header */}
                  <div className="tw-px-[16px] tw-py-[10px] tw-bg-white tw-border-b-[0.5px] tw-border-gray-100">
                    <span className="tw-text-[13px] tw-font-medium tw-text-gray-800 tw-leading-tight">
                      {t('Search.ResultsFor')}{' '}
                      <span className="tw-text-gray-500">"{searchValue}"</span>
                    </span>
                  </div>

                  {/* Scrollable Results Container */}
                  <div className="tw-max-h-[320px] tw-overflow-y-auto tw-w-full tw-custom-scrollbar">
                    {isSearching ? (
                      <div className="tw-p-12 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
                        <div className="tw-w-6 tw-h-6 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                        <p className="tw-text-s tw-text-gray-400">
                          {t('TimeClockApp_Search')}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Collaborateurs Section */}
                        {usersList && usersList.length > 0 ? (
                          <div className="tw-py-1">
                            <p className="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-my-2 tw-px-4">
                              {t('Search.collaborateurs')}
                            </p>
                            {usersList.map((user, index) => (
                              <div key={index} className="tw-group">
                                {usersOnly ? (
                                  <button
                                    onClick={() => handleUserSelect(user)}
                                    className="tw-w-full tw-text-start tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-3 hover:tw-bg-[#F2F9F6] tw-transition-all tw-border-b tw-border-gray-50 last:tw-border-none"
                                  >
                                    <UserShortCut
                                      src={`upload/profileimages/${user.value}/${user.imagePath}`}
                                      alt=""
                                      className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#E8F3ED] tw-text-[#1F8A5B] tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-s tw-shrink-0"
                                    />
                                    <div className="tw-flex-1 tw-min-w-0">
                                      <span className="tw-text-s tw-font-semibold tw-text-gray-900 tw-truncate group-hover:tw-text-primary tw-transition-colors tw-block">
                                        {user.text}
                                      </span>
                                    </div>
                                  </button>
                                ) : (
                                  <Link
                                    href={`/employee/${user.value}/profile/public`}
                                    onClick={handleClose}
                                    className="tw-w-full tw-text-start tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-3 hover:tw-bg-[#F2F9F6] tw-transition-all tw-border-b tw-border-gray-50 tw-no-underline last:tw-border-none"
                                  >
                                    <UserShortCut
                                      src={`upload/profileimages/${user.value}/${user.imagePath}`}
                                      alt=""
                                      className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#E8F3ED] tw-text-[#1F8A5B] tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-s tw-shrink-0"
                                    />
                                    <div className="tw-flex-1 tw-min-w-0">
                                      <span className="tw-text-s tw-font-semibold tw-text-gray-900 tw-truncate group-hover:tw-text-primary tw-transition-colors tw-block">
                                        {user.text}
                                      </span>
                                    </div>
                                  </Link>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {/* Documents Section */}
                        {!usersOnly &&
                        documentList &&
                        documentList.length > 0 ? (
                          <div className="tw-py-1">
                            <p className="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-my-2 tw-px-4">
                              {t('Search.Documents')}
                            </p>
                            {documentList.map((doc, index) => (
                              <button
                                key={index}
                                onClick={() =>
                                  handleOpenFile(
                                    doc.imagePath as string,
                                    doc.text as string,
                                    doc.type as string,
                                    doc.value as number,
                                  )
                                }
                                className="tw-w-full tw-text-start tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-3 hover:tw-bg-gray-50 tw-transition-all tw-border-b tw-border-gray-50 group-last/doc:tw-border-none last:tw-border-none"
                              >
                                <div className="tw-w-8 tw-h-8 tw-rounded-xl tw-bg-orange-50 tw-text-orange-500 tw-flex tw-items-center tw-justify-center tw-shrink-0 group-hover:tw-bg-orange-500 group-hover:tw-text-white tw-transition-all tw-border tw-border-orange-100 group-hover:tw-border-orange-500">
                                  <LucideIcon name="FileText" size={18} />
                                </div>
                                <div className="tw-flex-1 tw-min-w-0">
                                  <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate group-hover:tw-text-primary tw-transition-colors tw-block">
                                    {doc.text}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : null}

                        {!(
                          usersList?.length ||
                          (!usersOnly && documentList?.length)
                        ) && (
                          <div className="tw-py-12 tw-text-center tw-px-6">
                            <img
                              src="/icons/search-no-results.svg"
                              alt=""
                              className="tw-mx-auto tw-mb-3 tw-opacity-20 tw-w-12"
                            />
                            <p className="tw-text-s tw-text-gray-500 tw-font-medium">
                              {t(
                                'Search.NoResultsFound',
                                'Aucun résultat trouvé',
                              )}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Tippy content={t('TimeClockApp_Search')}>
              <button
                onClick={() => setIsSearchOpen(true)}
                className="tw-hidden md:tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-50 hover:tw-text-gray-900 tw-transition-colors tw-duration-150"
              >
                <SearchIcon size={16} strokeWidth={1.8} />
              </button>
            </Tippy>
          )}
        </div>
      </div>
    );
  },
);

SearchContainer.displayName = 'SearchContainer';
export default SearchContainer;
